/* vc-home-marquee.js — infinite drifting carousel used by "Shop by fabric"
   (right to left, 24px/s) and "In her words" (left to right, 18px/s).

   The item list is rendered three times in one flex track; the offset is
   wrapped by one set width so the loop never ends. Hover eases the drift to a
   stop, drag follows the pointer 1:1 and keeps its release velocity, easing
   back to the normal drift over about three seconds.

   Config comes from data attributes on the viewport element:
     data-vch-marquee          marks the viewport
     data-speed="-24"         px/s; negative drifts right to left
     data-count="6"           items in ONE set (the track holds three sets)
     data-gap="16"            px gap between items
     data-fit="1"             size items so a whole number fits the viewport
     data-card="400"          fixed item width when not fitting
     data-pause-hover="1"     ease to a stop under the mouse
   Loaded with defer; depends on vc-home-motion.js for the shared rAF loop. */
(function () {
  'use strict';

  /* Two sections reference this file, so the tag is on the page twice and the
     browser runs it twice. Bail on the second run. */
  if (window.__vcMarqueeLoaded) return;
  window.__vcMarqueeLoaded = true;

  var MAX_FLING = 2400; /* px/s */
  var DRAG_START = 4; /* px before we capture the pointer */
  var CLICK_CANCEL = 6; /* px of movement that suppresses the click */

  function num(el, name, dflt) {
    var v = parseFloat(el.getAttribute(name));
    return isNaN(v) ? dflt : v;
  }

  function setup(vp) {
    if (vp.__vcMarquee) return;
    var track = vp.querySelector('[data-vch-marquee-track]');
    if (!track) return;
    vp.__vcMarquee = true;

    var VC = window.VCHome || {};
    var reduce = !!VC.reduce;

    var m = {
      speed: num(vp, 'data-speed', -24),
      count: num(vp, 'data-count', 6),
      gap: num(vp, 'data-gap', 16),
      card: num(vp, 'data-card', 400),
      fit: vp.getAttribute('data-fit') === '1',
      visD: num(vp, 'data-vis-d', 5),
      visT: num(vp, 'data-vis-t', 3),
      visM: num(vp, 'data-vis-m', 1.8),
      pauseHover: vp.getAttribute('data-pause-hover') !== '0',
      pos: null,
      vel: 0,
      setW: 0,
      last: 0,
      hover: false,
      focus: false,
      drag: null,
      moved: 0
    };

    /* ---- measure --------------------------------------------------------- */
    function measure() {
      var W = vp.clientWidth;
      if (!W) return false;

      /* Read the gap from the stylesheet rather than the data attribute: both
         the gap and the card width change at the mobile breakpoint, and a
         stale number here makes the loop jump. */
      var cs = getComputedStyle(track);
      var gap = parseFloat(cs.columnGap);
      if (isNaN(gap)) gap = parseFloat(cs.gap);
      if (isNaN(gap)) gap = m.gap;

      var card;
      if (m.fit) {
        var vis = W < 640 ? m.visM : W < 1000 ? m.visT : m.visD;
        card = (W - gap * (Math.ceil(vis) - 1)) / vis;
        vp.style.setProperty('--vch-card-w', card + 'px');
      } else {
        /* Fixed-width items: trust what they actually render at. */
        var first = track.firstElementChild;
        card = first ? first.getBoundingClientRect().width : 0;
        if (!card) card = m.card;
      }

      var old = m.setW;
      m.setW = (card + gap) * m.count;
      if (!m.setW) return false;
      /* Start on the middle set so the track can travel either way. */
      if (m.pos === null) m.pos = -m.setW;
      else if (old) m.pos = (m.pos / old) * m.setW;
      return true;
    }

    /* ---- per-frame ------------------------------------------------------- */
    function step(t) {
      var dt = m.last ? Math.min(0.05, (t - m.last) / 1000) : 0;
      m.last = t;
      if (!m.setW && !measure()) return;

      if (!m.drag) {
        var base = Math.abs(m.speed) || 1;
        var stopped = (m.hover && m.pauseHover) || m.focus || reduce;
        var target = stopped ? 0 : m.speed;
        /* A fling decays slowly; a hover stop is quick; otherwise steady. */
        var tau = Math.abs(m.vel) > base * 3 ? 1.2 : stopped ? 0.35 : 1.0;
        m.vel += (target - m.vel) * (1 - Math.exp(-dt / tau));
        m.pos += m.vel * dt;
      }

      while (m.pos > 0) m.pos -= m.setW;
      while (m.pos <= -2 * m.setW) m.pos += m.setW;

      track.style.transform = 'translate3d(' + m.pos.toFixed(2) + 'px, 0, 0)';
    }
    step.reset = function () {
      m.last = 0;
    };

    /* ---- pointer --------------------------------------------------------- */
    function endDrag() {
      var d = m.drag;
      if (!d) return;
      m.drag = null;
      m.moved = d.moved;
      vp.classList.remove('is-dragging');
      /* A pointer that paused before release should not fling. */
      var idle = performance.now() - d.lt > 120;
      m.vel = idle ? 0 : Math.max(-MAX_FLING, Math.min(MAX_FLING, d.v));
    }

    vp.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'mouse') m.hover = true;
    });

    vp.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'mouse') m.hover = false;
      if (m.drag && !m.drag.cap) endDrag();
    });

    vp.addEventListener('pointerdown', function (e) {
      if (m.pos === null) return;
      if (e.button !== undefined && e.button !== 0) return;
      m.drag = {
        x: e.clientX,
        pos: m.pos,
        moved: 0,
        lx: e.clientX,
        lt: performance.now(),
        v: 0,
        cap: false
      };
      m.vel = 0;
    });

    vp.addEventListener('pointermove', function (e) {
      var d = m.drag;
      if (!d) return;
      var dx = e.clientX - d.x;
      d.moved = Math.max(d.moved, Math.abs(dx));
      if (d.moved > DRAG_START && !d.cap) {
        d.cap = true;
        vp.classList.add('is-dragging');
        try {
          vp.setPointerCapture(e.pointerId);
        } catch (err) {}
      }
      m.pos = d.pos + dx;
      var now = performance.now();
      var s = (now - d.lt) / 1000;
      if (s > 0) d.v = d.v * 0.5 + ((e.clientX - d.lx) / s) * 0.5;
      d.lx = e.clientX;
      d.lt = now;
    });

    vp.addEventListener('pointerup', endDrag);
    vp.addEventListener('pointercancel', endDrag);

    /* A drag must not count as a click on the card underneath. */
    vp.addEventListener(
      'click',
      function (e) {
        if (m.moved > CLICK_CANCEL) {
          e.preventDefault();
          e.stopPropagation();
        }
        m.moved = 0;
      },
      true
    );

    vp.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    /* Keyboard users get the same courtesy as mouse users. */
    vp.addEventListener('focusin', function () {
      m.focus = true;
    });
    vp.addEventListener('focusout', function () {
      if (!vp.contains(document.activeElement)) m.focus = false;
    });

    /* ---- resize ---------------------------------------------------------- */
    var rt = 0;
    function onResize() {
      clearTimeout(rt);
      rt = setTimeout(function () {
        m.setW = 0;
        measure();
      }, 120);
    }
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    measure();
    if (VC.addJob) {
      vp.__vcStop = VC.addJob(step);
    }
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-vch-marquee]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Theme editor support */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-vch-marquee]').forEach(function (vp) {
      if (vp.__vcStop) vp.__vcStop();
    });
  });
})();
